import React, { useState } from 'react';
import { Button, ModalPortal } from './ui';
import { KindBadge } from './KindBadge';
import { VerificationBadge } from './VerificationBadge';
import type { ItemVerificationStatus, RecordKind } from '../types';

export interface VerifyTarget {
  kind: RecordKind;
  id: string;
  title: string;
  status?: ItemVerificationStatus;
}

interface VerifyModalProps {
  target: VerifyTarget;
  submitting: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (basis: string) => Promise<void>;
}

export const VerifyModal: React.FC<VerifyModalProps> = ({
  target,
  submitting,
  error,
  onClose,
  onSubmit,
}) => {
  const [basis, setBasis] = useState('');
  const tooShort = basis.trim().length < 10;

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-5 sm:p-6 m-0 sm:m-4">
        <h2 className="text-lg font-bold text-gray-900">Verify this record</h2>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <KindBadge kind={target.kind} size="sm" />
          <p className="text-sm text-gray-500">{target.title}</p>
        </div>
        <div className="mt-2">
          <VerificationBadge status={target.status} />
        </div>

        <label className="block mt-4 text-sm font-medium text-gray-700">
          On what basis is this verified?
          <textarea
            value={basis}
            onChange={e => setBasis(e.target.value)}
            rows={4}
            className="mt-1.5 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder="Describe the evidence or checks you used (at least 10 characters)."
          />
        </label>
        {tooShort && basis.length > 0 && (
          <p className="text-xs text-amber-700 mt-1">Please add a bit more detail.</p>
        )}
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

        <div className="flex gap-3 mt-5">
          <Button
            onClick={() => onSubmit(basis.trim())}
            disabled={tooShort}
            loading={submitting}
            className="flex-1"
          >
            Verify
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={submitting} className="flex-1">
            Cancel
          </Button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};
